import React, { Suspense, Fragment, lazy } from 'react';
import { Switch, Redirect, Route } from 'react-router-dom';
import DashboardLayout from 'src/layouts/DashboardLayout';
import MainLayout from 'src/layouts/MainLayout';
import LoadingScreen from 'src/components/LoadingScreen';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';

export const renderRoutes = (routes = []) => (
  <Suspense fallback={<LoadingScreen />}>
    <Switch>
      {routes.map((route, i) => {
        const Guard = route.guard || Fragment;
        const Layout = route.layout || Fragment;
        const Component = route.component;

        return (
          <Route
            key={i}
            path={route.path}
            exact={route.exact}
            render={props => (
              <Guard>
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    <Component {...props} />
                  )}
                </Layout>
              </Guard>
            )}
          />
        );
      })}
    </Switch>
  </Suspense>
);

const routes = [
  {
    exact: true,
    path: '/404',
    component: lazy(() => import('src/views/errors/NotFoundView'))
  },
  // 2026 admin redesign — component catalog + first pages (mock data, no auth
  // so they're reviewable without a backend). Temporary; remove after rollout.
  {
    exact: true,
    path: '/redesign-kit',
    component: lazy(() => import('src/views/redesign/RedesignKit'))
  },
  {
    exact: true,
    path: '/redesign-kit/dashboard',
    component: lazy(() => import('src/views/redesign/Dashboard'))
  },
  {
    exact: true,
    path: '/redesign-kit/volunteers',
    component: lazy(() => import('src/views/redesign/VolunteersList'))
  },
  {
    exact: true,
    path: '/redesign-kit/volunteer-profile',
    component: lazy(() => import('src/views/redesign/VolunteerProfile'))
  },
  {
    exact: true,
    path: '/redesign-kit/activity-review',
    component: lazy(() => import('src/views/redesign/ActivityReview'))
  },
  {
    exact: true,
    path: '/redesign-kit/courses',
    component: lazy(() => import('src/views/redesign/CoursesList'))
  },
  {
    exact: true,
    path: '/redesign-kit/create-course',
    component: lazy(() => import('src/views/redesign/CreateCourse'))
  },
  {
    exact: true,
    path: '/redesign-kit/categories',
    component: lazy(() => import('src/views/redesign/CategoriesList'))
  },
  {
    exact: true,
    path: '/redesign-kit/certificates',
    component: lazy(() => import('src/views/redesign/CertificatesList'))
  },
  {
    exact: true,
    path: '/redesign-kit/create-activity',
    component: lazy(() => import('src/views/redesign/CreateActivity'))
  },
  {
    exact: true,
    path: '/redesign-kit/create-team',
    component: lazy(() => import('src/views/redesign/CreateTeam'))
  },
  {
    exact: true,
    path: '/redesign-kit/create-organisation',
    component: lazy(() => import('src/views/redesign/CreateOrganisation'))
  },
  {
    exact: true,
    path: '/redesign-kit/create-beneficiary',
    component: lazy(() => import('src/views/redesign/CreateBeneficiary'))
  },
  {
    exact: true,
    path: '/redesign-kit/create-programme',
    component: lazy(() => import('src/views/redesign/CreateProgramme'))
  },
  // Edit variants — same form components, prefilled (detected via /edit-* path).
  {
    exact: true,
    path: '/redesign-kit/edit-activity',
    component: lazy(() => import('src/views/redesign/CreateActivity'))
  },
  {
    exact: true,
    path: '/redesign-kit/edit-team',
    component: lazy(() => import('src/views/redesign/CreateTeam'))
  },
  {
    exact: true,
    path: '/redesign-kit/edit-organisation',
    component: lazy(() => import('src/views/redesign/CreateOrganisation'))
  },
  {
    exact: true,
    path: '/redesign-kit/edit-beneficiary',
    component: lazy(() => import('src/views/redesign/CreateBeneficiary'))
  },
  {
    exact: true,
    path: '/redesign-kit/edit-programme',
    component: lazy(() => import('src/views/redesign/CreateProgramme'))
  },
  {
    exact: true,
    path: '/redesign-kit/users',
    component: lazy(() => import('src/views/redesign/UsersList'))
  },
  {
    exact: true,
    path: '/redesign-kit/settings',
    component: lazy(() => import('src/views/redesign/Settings'))
  },
  {
    exact: true,
    path: '/redesign-kit/schools',
    component: lazy(() => import('src/views/redesign/SchoolsList'))
  },
  {
    exact: true,
    path: '/redesign-kit/activities',
    component: lazy(() => import('src/views/redesign/ActivitiesList'))
  },
  {
    exact: true,
    path: '/redesign-kit/teams',
    component: lazy(() => import('src/views/redesign/TeamsList'))
  },
  {
    exact: true,
    path: '/redesign-kit/organisations',
    component: lazy(() => import('src/views/redesign/OrganisationsList'))
  },
  {
    exact: true,
    path: '/redesign-kit/programmes',
    component: lazy(() => import('src/views/redesign/ProgrammesList'))
  },
  {
    exact: true,
    path: '/redesign-kit/awards',
    component: lazy(() => import('src/views/redesign/AwardsList'))
  },
  {
    exact: true,
    path: '/redesign-kit/beneficiaries',
    component: lazy(() => import('src/views/redesign/BeneficiariesList'))
  },
  {
    exact: true,
    path: '/redesign-kit/incidents',
    component: lazy(() => import('src/views/redesign/IncidentsList'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/login',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/forgot-password',
    component: lazy(() => import('src/views/auth/ForgotPasswordView'))
  },
  {
    exact: true,
    path: '/login-unprotected',
    component: lazy(() => import('src/views/auth/LoginView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/reset-request',
    component: lazy(() => import('src/views/auth/ResetRequestView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/reset-password',
    component: lazy(() => import('src/views/auth/ResetView'))
  },
  {
    exact: true,
    guard: GuestGuard,
    path: '/register',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  {
    exact: true,
    path: '/register-unprotected',
    component: lazy(() => import('src/views/auth/RegisterView'))
  },
  // ---- 2026 redesign CUTOVER: the browse surfaces (dashboard + list pages +
  // Users/Settings) now render the redesign views. Guarded (auth + store bootstrap
  // via JWTAuthContext) but NO DashboardLayout — each redesign view brings its own
  // AdminShell (sidebar + top bar), so wrapping in DashboardLayout would double the
  // chrome. Placed before /dashboard & /app and `exact`, so Switch matches these
  // first while detail/create/other routes fall through to the old /app views
  // (unchanged — the redesign create/edit forms are still mock, no submit yet).
  { exact: true, guard: AuthGuard, path: '/app/reports/dashboard', component: lazy(() => import('src/views/redesign/Dashboard')) },
  { exact: true, guard: AuthGuard, path: '/app/management/volunteers', component: lazy(() => import('src/views/redesign/VolunteersList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/volunteers/:volunteerId/profile', component: lazy(() => import('src/views/redesign/VolunteerProfile')) },
  { exact: true, guard: AuthGuard, path: '/app/management/teams', component: lazy(() => import('src/views/redesign/TeamsList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/teams/create', component: lazy(() => import('src/views/redesign/CreateTeam')) },
  { exact: true, guard: AuthGuard, path: '/app/management/teams/:teamId/overview', component: lazy(() => import('src/views/redesign/TeamDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/teams/:teamId/edit', component: lazy(() => import('src/views/redesign/CreateTeam')) },
  { exact: true, guard: AuthGuard, path: '/app/management/activities', component: lazy(() => import('src/views/redesign/ActivitiesList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/activities/create', component: lazy(() => import('src/views/redesign/CreateActivity')) },
  { exact: true, guard: AuthGuard, path: '/app/management/activities/review-queue', component: lazy(() => import('src/views/redesign/ReviewQueue')) },
  { exact: true, guard: AuthGuard, path: '/app/management/activities/review-queue/:reviewId', component: lazy(() => import('src/views/redesign/ActivityReview')) },
  { exact: true, guard: AuthGuard, path: '/app/management/activities/:activityId/overview', component: lazy(() => import('src/views/redesign/ActivityDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/institutions', component: lazy(() => import('src/views/redesign/SchoolsList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/organisations', component: lazy(() => import('src/views/redesign/OrganisationsList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/organisations/create', component: lazy(() => import('src/views/redesign/CreateOrganisation')) },
  { exact: true, guard: AuthGuard, path: '/app/management/institutions/:institutionId/overview', component: lazy(() => import('src/views/redesign/InstitutionDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/programmes', component: lazy(() => import('src/views/redesign/ProgrammesList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/programmes/create', component: lazy(() => import('src/views/redesign/CreateProgramme')) },
  { exact: true, guard: AuthGuard, path: '/app/management/programmes/:programmesId/overview', component: lazy(() => import('src/views/redesign/ProgrammeDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/awards', component: lazy(() => import('src/views/redesign/AwardsList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/awards/:awardId/overview', component: lazy(() => import('src/views/redesign/AwardDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/beneficiaries', component: lazy(() => import('src/views/redesign/BeneficiariesList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/beneficiaries/create', component: lazy(() => import('src/views/redesign/CreateBeneficiary')) },
  { exact: true, guard: AuthGuard, path: '/app/management/beneficiaries/:beneficiaryId/overview', component: lazy(() => import('src/views/redesign/BeneficiaryDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/beneficiaries/:beneficiaryId/edit', component: lazy(() => import('src/views/redesign/CreateBeneficiary')) },
  { exact: true, guard: AuthGuard, path: '/app/management/incidents', component: lazy(() => import('src/views/redesign/IncidentsList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/incidents/:incidentId/overview', component: lazy(() => import('src/views/redesign/IncidentDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/courses', component: lazy(() => import('src/views/redesign/CoursesList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/courses/create', component: lazy(() => import('src/views/redesign/CreateCourse')) },
  { exact: true, guard: AuthGuard, path: '/app/management/courses/:courseId/overview', component: lazy(() => import('src/views/redesign/CourseDetail')) },
  { exact: true, guard: AuthGuard, path: '/app/management/courses/:courseId/edit', component: lazy(() => import('src/views/redesign/CreateCourse')) },
  { exact: true, guard: AuthGuard, path: '/app/management/categories', component: lazy(() => import('src/views/redesign/CategoriesList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/certificates', component: lazy(() => import('src/views/redesign/CertificatesList')) },
  { exact: true, guard: AuthGuard, path: '/app/management/users', component: lazy(() => import('src/views/redesign/UsersList')) },
  { exact: true, guard: AuthGuard, path: '/app/settings', component: lazy(() => import('src/views/redesign/Settings')) },
  // Tools pages cut over to the AdminShell chrome (wrap the existing flows).
  { exact: true, guard: AuthGuard, path: '/app/management/volunteers/topVolunteers', component: lazy(() => import('src/views/redesign/TopVolunteersPage')) },
  { exact: true, guard: AuthGuard, path: '/app/management/institutions/school-directory-import', component: lazy(() => import('src/views/redesign/SchoolImportPage')) },
  { exact: true, guard: AuthGuard, path: '/app/support-tickets', component: lazy(() => import('src/views/redesign/SupportTicketsPage')) },
  { exact: true, guard: AuthGuard, path: '/app/knowledge', component: lazy(() => import('src/views/redesign/KnowledgeBasePage')) },
  {
    path: '/dashboard',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/dashboard',
        component: lazy(() =>
          import('src/views/reports/DashboardAlternativeView')
        )
      }
    ]
  },
  {
    path: '/app',
    guard: AuthGuard,
    layout: DashboardLayout,
    routes: [
      {
        exact: true,
        path: '/app/support-tickets',
        component: lazy(() => import('src/components/SupportTickets/SupportTicketListPage'))
      },
      {
        exact: true,
        path: '/app/support-tickets/:ticketId',
        component: lazy(() => import('src/components/SupportTickets/SupportTicketPage'))
      },
      {
        exact: true,
        path: '/app/knowledge',
        component: lazy(() => import('src/components/ChatConfig/KnowledgeBasePage'))
      },
      {
        exact: true,
        path: '/app/account',
        component: () => <Redirect to="/app/reports" />
      },
      {
        exact: true,
        path: '/app/calendar',
        component: lazy(() => import('src/views/calendar/CalendarView'))
      },
      {
        exact: true,
        path: ['/app/chat/new', '/app/chat/:threadKey'],
        component: lazy(() => import('src/views/chat/ChatView'))
      },
      {
        exact: true,
        path: '/app/chat',
        component: () => <Redirect to="/app/chat/new" />
      },
      {
        exact: true,
        path: '/app/extra/charts/apex',
        component: lazy(() => import('src/views/extra/charts/ApexChartsView'))
      },
      {
        exact: true,
        path: '/app/extra/forms/formik',
        component: lazy(() => import('src/views/extra/forms/FormikView'))
      },
      {
        exact: true,
        path: '/app/extra/forms/redux',
        component: lazy(() => import('src/views/extra/forms/ReduxFormView'))
      },
      {
        exact: true,
        path: '/app/extra/editors/draft-js',
        component: lazy(() => import('src/views/extra/editors/DraftEditorView'))
      },
      {
        exact: true,
        path: '/app/extra/editors/quill',
        component: lazy(() => import('src/views/extra/editors/QuillEditorView'))
      },
      {
        exact: true,
        path: '/app/kanban',
        component: lazy(() => import('src/views/kanban/KanbanView'))
      },
      {
        exact: true,
        path: [
          '/app/mail/label/:customLabel/:mailId?',
          '/app/mail/:systemLabel/:mailId?'
        ],
        component: lazy(() => import('src/views/mail/MailView'))
      },
      {
        exact: true,
        path: '/app/mail',
        component: () => <Redirect to="/app/mail/all" />
      },
      {
        exact: true,
        path: '/app/management/types',
        component: lazy(() => import('src/views/types'))
      },
      {
        exact: true,
        path: '/app/management/teams',
        component: lazy(() => import('src/views/teams/TeamListView'))
      },
      {
        exact: true,
        path: '/app/management/teams/:teamId',
        component: lazy(() => import('src/views/teams/TeamDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/volunteers/topVolunteers',
        component: lazy(() => import('src/views/volunteer/TopVolunteer'))
      },
      {
        exact: true,
        path: '/app/management/volunteers',
        component: lazy(() => import('src/views/volunteer/VolunteerListView'))
      },
      {
        exact: true,
        path: '/app/management/volunteers/:volunteerId',
        component: lazy(() => import('src/views/volunteer/VolunteerEditView'))
      },
      {
        exact: true,
        path: '/app/management/volunteers/:volunteerId/add_activity',
        component: lazy(() =>
          import('src/views/volunteer/VolunteerAddActivity')
        )
      },
      {
        exact: true,
        path: '/app/management/volunteers/create',
        component: lazy(() => import('src/views/volunteer/VolunteerEditView'))
      },
      {
        exact: true,
        path: '/app/management/beneficiaries/',
        component: lazy(() =>
          import('src/views/beneficiaries/BeneficiaryListView')
        )
      },
      {
        exact: true,
        path: '/app/management/beneficiaries/create',
        component: lazy(() =>
          import('src/views/beneficiaries/BeneficiaryEditView')
        )
      },
      {
        exact: true,
        path: '/app/management/beneficiaries/:beneficiaryId',
        component: lazy(() =>
          import('src/views/beneficiaries/BeneficiaryEditView')
        )
      },
      {
        exact: true,
        path: '/app/management/incidents',
        component: lazy(() => import('src/views/incidents/IncidentListView'))
      },
      {
        exact: true,
        path: '/app/management/incidents/create',
        component: lazy(() => import('src/views/incidents/IncidentCreateView'))
      },
      {
        exact: true,
        path: '/app/management/incidents/:incidentId',
        component: lazy(() => import('src/views/incidents/IncidentDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/orders',
        component: lazy(() => import('src/views/order/OrderListView'))
      },
      {
        exact: true,
        path: '/app/management/orders/:orderId',
        component: lazy(() => import('src/views/order/OrderDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/activities',
        component: lazy(() => import('src/views/activities/ActivityListView'))
      },
      {
        exact: true,
        path: '/app/management/awards',
        component: lazy(() => import('src/views/awards/AwardsListView'))
      },
      {
        exact: true,
        path: '/app/management/awards/:awardId',
        component: lazy(() => import('src/views/awards/AwardsDetailsView'))
      },
      {
        exact: true,
        path: '/app/management/institutions/create',
        component: lazy(() =>
          import('src/views/institutions/InstitutionsDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/institutions/school-directory-import',
        component: lazy(() =>
          import('src/views/institutions/SchoolDirectoryImportView')
        )
      },
      {
        exact: true,
        path: '/app/management/institutions/:institutionId',
        component: lazy(() =>
          import('src/views/institutions/InstitutionsDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/institutions',
        component: lazy(() =>
          import('src/views/institutions/InstitutionsListView')
        )
      },
      {
        exact: true,
        path: '/app/management/programmes/:programmesId',
        component: lazy(() =>
          import('src/views/programmes/ProgrammesDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/programmes/create',
        component: lazy(() =>
          import('src/views/programmes/ProgrammesDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/programmes',
        component: lazy(() => import('src/views/programmes/ProgrammesListView'))
      },
      {
        exact: true,
        path: '/app/management/activities/create',
        component: lazy(() =>
          import('src/views/activities/ActivityDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management/activities/review-queue',
        component: lazy(() =>
          import('src/views/activities/ActivityBacklogReviewQueue')
        )
      },
      {
        exact: true,
        path: '/app/management/activities/:activityId',
        component: lazy(() =>
          import('src/views/activities/ActivityDetailsView')
        )
      },
      {
        exact: true,
        path: '/app/management',
        component: () => <Redirect to="/app/management/volunteers" />
      },
      {
        exact: true,
        path: '/app/projects/overview',
        component: lazy(() => import('src/views/project/OverviewView'))
      },
      {
        exact: true,
        path: '/app/projects/browse',
        component: lazy(() => import('src/views/project/ProjectBrowseView'))
      },
      {
        exact: true,
        path: '/app/projects/create',
        component: lazy(() => import('src/views/project/ProjectCreateView'))
      },
      {
        exact: true,
        path: '/app/projects/:id',
        component: lazy(() => import('src/views/project/ProjectDetailsView'))
      },
      {
        exact: true,
        path: '/app/projects',
        component: () => <Redirect to="/app/projects/browse" />
      },
      {
        exact: true,
        path: '/app/reports/dashboard',
        component: lazy(() =>
          import('src/views/reports/DashboardAlternativeView')
        )
      },
      {
        exact: true,
        path: '/dashboard',
        component: lazy(() =>
          import('src/views/reports/DashboardAlternativeView')
        )
      },
      {
        exact: true,
        path: '/app/reports',
        component: () => <Redirect to="/app/reports/dashboard" />
      },
      {
        exact: true,
        path: '/app/social/feed',
        component: lazy(() => import('src/views/social/FeedView'))
      },
      {
        exact: true,
        path: '/app/social/profile',
        component: lazy(() => import('src/views/social/ProfileView'))
      },
      {
        exact: true,
        path: '/app/social',
        component: () => <Redirect to="/app/social/profile" />
      },
      {
        exact: true,
        path: '/app',
        component: () => <Redirect to="/app/reports/dashboard" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  },
  {
    path: '*',
    layout: MainLayout,
    routes: [
      {
        exact: true,
        path: '/',
        component: lazy(() => import('src/views/auth/LoginView'))
      },
      {
        exact: true,
        path: '/support-tickets/:ticketId',
        component: props => <Redirect to={`/app/support-tickets/${props.match.params.ticketId}`} />
      },
      {
        exact: true,
        path: '/support-tickets',
        component: () => <Redirect to="/app/support-tickets" />
      },
      {
        component: () => <Redirect to="/404" />
      }
    ]
  }
];

export default routes;
